import axios from "axios";

export const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ?? process.env.REACT_APP_BACKEND_URL_DEV;
// export const API_BASE_URL = "http://127.0.0.1:5000";
axios.defaults.baseURL = API_BASE_URL;

/**
 * Common helper to make an axios request with the BASIC_AUTH_HEADER and query parameters.
 *
 * @param {string} method - HTTP method (e.g., "post").
 * @param {string} endpoint - The API endpoint.
 * @param {object} body - Data to send in the request body.
 * @param {object} params - Query parameters to attach to the URL.
 * @returns {Promise} Axios response.
 */
const makeAxiosRequest = async (method, endpoint, body = {}, params = {}) => {
  const headers = {
    Authorization: sessionStorage.getItem("BasicToken"),
    "Content-Type": "application/json",
  };

  return axios.request({
    method,
    url: endpoint,
    data: body,
    params,
    headers,
  });
};

/**
 * Converts a base64 string into an ArrayBuffer.
 *
 * @param {string} base64Str - The base64 encoded string.
 * @returns {ArrayBuffer} The resulting ArrayBuffer.
 */
const base64ToArrayBuffer = (base64Str) => {
  const binaryStr = window.atob(base64Str);
  const len = binaryStr.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Converts an ArrayBuffer into a base64 encoded string.
 *
 * @param {ArrayBuffer} buffer - The ArrayBuffer to convert.
 * @returns {string} The base64 encoded string.
 */
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binaryStr = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binaryStr += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binaryStr);
};

/**
 * Decrypts a base64-encoded ciphertext (with IV prepended) using AES-CBC.
 *
 * @param {string} ciphertextB64 - Base64 encoded ciphertext (IV + ciphertext).
 * @param {ArrayBuffer} keyBuffer - The AES key as an ArrayBuffer.
 * @returns {Promise<string>} A promise that resolves to the decrypted plaintext.
 */
const decryptAESCBC = async (ciphertextB64, keyBuffer) => {
  // Convert base64 ciphertext to ArrayBuffer.
  const ciphertextWithIv = base64ToArrayBuffer(ciphertextB64);
  // Extract IV (first 16 bytes) and the actual ciphertext.
  const iv = ciphertextWithIv.slice(0, 16);
  const ciphertext = ciphertextWithIv.slice(16);

  // Import the key for AES-CBC decryption.
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyBuffer,
    { name: "AES-CBC" },
    false, // non-extractable
    ["decrypt"]
  );

  // Decrypt the ciphertext.
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: "AES-CBC", iv: new Uint8Array(iv) },
    cryptoKey,
    ciphertext
  );

  // Convert decrypted ArrayBuffer to string.
  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
};

/**
 * Runs the key exchange process to retrieve the decrypted CSEK key.
 *
 * Process:
 *   1. POST { step: "1" } → Receive { temp_id, encrypted_key }
 *   2. POST { step: "2", temp_id } → Receive { encrypted_actual_key }
 *   3. POST { step: "3", temp_id } → Receive { decryption_key }
 *
 * Then:
 *   - Use decryption_key (ephemeral key Z) to decrypt encrypted_key, retrieving ephemeralKeyX as a base64 string.
 *   - Convert ephemeralKeyX from base64 to a binary key.
 *   - Use ephemeralKeyX to decrypt encrypted_actual_key to recover the actual CSEK key.
 *
 * @returns {Promise<string>} The decrypted CSEK key (base64 string).
 */


export const getDecryptedCSEKKey = async () => {
  try {
    // Retrieve email and sessionId from sessionStorage.
    const email = sessionStorage.getItem("mail");
    const queryParams = { emailId: email, currentSessionId: sessionStorage.getItem("sessionId") };

    if (!email || !sessionStorage.getItem("sessionId") || !sessionStorage.getItem("BasicToken")) return null;

    // STEP 1: Retrieve ephemeral values.
    const {
      data: { temp_id: tempId, encrypted_key: encryptedEphemeralKey },
    } = await makeAxiosRequest("post", "/secured-nexus-bridge", { step: "1" }, queryParams);
    if (!tempId || !encryptedEphemeralKey) {
      throw new Error("Invalid response in step 1");
    }

    // STEP 2: Retrieve the actual key encrypted with ephemeral key X.
    const {
      data: { encrypted_actual_key: encryptedActualKey },
    } = await makeAxiosRequest("post", "/secured-nexus-bridge", { step: "2", temp_id: tempId }, queryParams);
    if (!encryptedActualKey) {
      throw new Error("Invalid response in step 2");
    }

    // STEP 3: Retrieve the decryption key (ephemeral key Z).
    const {
      data: { decryption_key: decryptionKey },
    } = await makeAxiosRequest("post", "/secured-nexus-bridge", { step: "3", temp_id: tempId }, queryParams);
    if (!decryptionKey) {
      throw new Error("Invalid response in step 3");
    }

    // Convert decryptionKey (ephemeral key Z) from base64 to an ArrayBuffer.
    const ephemeralKeyZBuffer = base64ToArrayBuffer(decryptionKey);

    // Use ephemeral key Z to decrypt encryptedEphemeralKey, retrieving ephemeralKeyX as a base64 string.
    const ephemeralKeyXBase64 = await decryptAESCBC(encryptedEphemeralKey, ephemeralKeyZBuffer);

    // Convert ephemeralKeyX from base64 to an ArrayBuffer.
    const ephemeralKeyXBuffer = base64ToArrayBuffer(ephemeralKeyXBase64);

    // Use ephemeralKeyX to decrypt encryptedActualKey to recover the actual CSEK key.
    const decryptedCSEKKey = await decryptAESCBC(encryptedActualKey, ephemeralKeyXBuffer);

    return decryptedCSEKKey;
  } catch (error) {
    console.error("Error during key exchange:", error);
    throw error;
  }
};

/* ------------------------------------------------------------------------- */
/*                         Caching the Decrypted Key                         */
/* ------------------------------------------------------------------------- */

// let cachedCSEKKey = null;

/**
 * Fetches and caches the decrypted CSEK key.
 * If the key is already cached, it is returned immediately.
 *
 * @returns {Promise<string>} The decrypted CSEK key (base64 string).
 */

// export const fetchAndCacheCSEKKey = async () => {
//   // Always fetch and cache the key if not already available.
//   if (!cachedCSEKKey) {
//     cachedCSEKKey = await getDecryptedCSEKKey();
//   }
//   return cachedCSEKKey;
// };

// /**
//  * Retrieves the cached CSEK key.
//  * If not already cached, it fetches and caches it first.
//  * Also, if the current pathname is "/" (login page), it returns null.
//  *
//  * @returns {Promise<string|null>} The cached decrypted CSEK key, or null if on the login page.
//  */
// export const getCachedCSEKKey = async () => {
//   // Always wait for the key to be fetched if not available.
//   if (!cachedCSEKKey) {
//     await fetchAndCacheCSEKKey();
//   }
//   return cachedCSEKKey;
// };

let cachedCSEKKey = null;
let cachedCSEKKeyPromise = null;

export const fetchAndCacheCSEKKey = async () => {
  // If a promise is already in-flight, return that promise.
  if (cachedCSEKKeyPromise) {
    return cachedCSEKKeyPromise;
  }
  // Otherwise, start the key exchange and cache the promise.
  cachedCSEKKeyPromise = getDecryptedCSEKKey().then((key) => {
    cachedCSEKKey = key;
    return key;
  });
  return cachedCSEKKeyPromise;
};

export const getCachedCSEKKey = async () => {
  if (!cachedCSEKKey) {
    await fetchAndCacheCSEKKey();
  }
  return cachedCSEKKey;
};